import { pdf, Document as PdfRenderableDocument } from "@react-pdf/renderer";
import { DownloadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
    Document as ReactPdfDocumentFile,
    Page as ReactPdfPageCanvas,
} from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import livePreviewApi from "@/api/livePreview";
import { Button } from "@/components/Button/Button";
import { Logo } from "@/components/Icons/Icons";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { loadFonts } from "@/components/PdfDocument/utils/loadFonts";
import { AppText } from "@/components/Text/AppText";
import { templates } from "@/pages/LivePreview/constants";
import { BankingTemplate } from "@/pages/LivePreview/templates/Banking";
import { ManagementTemplate } from "@/pages/LivePreview/templates/Management";
import { StudentTemplate } from "@/pages/LivePreview/templates/Student";
import { IResumePayload } from "@/pages/LivePreview/types";
import {
    A4_PAGE_HEIGHT_PIXELS,
    A4_PAGE_WIDTH_PIXELS,
} from "@/store/pages/constants";

const LoadingComponent = () => (
    <div
        className="loading-page flex justify-center bg-white"
        style={{
            height: A4_PAGE_HEIGHT_PIXELS,
            width: A4_PAGE_WIDTH_PIXELS,
        }}
    >
        <div className="mt-32">
            <LoadingSpinner />
        </div>
    </div>
);

export const LivePreview = ({
    resumeUrl,
    resumeData,
    activeTemplateId,
    loading,
    error,
}: {
    resumeUrl: string | null;
    resumeData: IResumePayload | null;
    activeTemplateId: string;
    loading: boolean;
    error: boolean;
}) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(1);
    const [fontsLoaded, setFontsLoaded] = useState(false);

    useEffect(() => {
        if (activeTemplateId) {
            const fontFamily = templates.find(
                template => template.id === activeTemplateId,
            )?.styles.GLOBAL.fontFamily;
            if (fontFamily) {
                loadFonts(fontFamily)
                    .then(() => {
                        setFontsLoaded(true);
                    })
                    .catch(err => {
                        console.log(" useEffect - err:", err);

                        setFontsLoaded(true);
                    });
            }
        }
    }, [fontsLoaded, activeTemplateId]);

    useEffect(() => {
        if (!fontsLoaded || !activeTemplateId || !resumeData) return;
        let url: string | null = null;
        const generatePdf = async () => {
            const template = templates.find(
                template => template.id === activeTemplateId,
            )!;
            const doc = (
                <PdfRenderableDocument>
                    {activeTemplateId === "banking" && (
                        <BankingTemplate
                            resumeData={resumeData}
                            styles={template.styles}
                        />
                    )}
                    {activeTemplateId === "management" && (
                        <ManagementTemplate
                            resumeData={resumeData}
                            styles={template.styles}
                        />
                    )}
                    {activeTemplateId === "student" && (
                        <StudentTemplate
                            resumeData={resumeData}
                            styles={template.styles}
                        />
                    )}
                </PdfRenderableDocument>
            );
            const blob = await pdf(doc).toBlob();
            url = URL.createObjectURL(blob);
            setPdfUrl(url);
        };
        generatePdf();
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [resumeData, activeTemplateId, fontsLoaded]);

    const onDownload = () => {
        if (!pdfUrl) return;
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = "resume.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        livePreviewApi.trackLivePreview({
            partnerId: "loopcv", // TODO: get partnerId dynamically
            url: resumeUrl ?? "",
            events: [
                {
                    type: "download",
                    timestamp: new Date(),
                    template: activeTemplateId ?? undefined,
                },
            ],
        });
    };

    return (
        <div className="relative">
            <nav className="fixed left-0 top-0 z-30 hidden h-16 w-full items-center justify-between border-b border-neutral-200 bg-white px-6 py-3 shadow-sm lg:flex">
                <a
                    className="flex items-center gap-2"
                    href="https://www.1template.io"
                    target="_blank"
                    rel="noreferrer"
                >
                    <AppText
                        variant="contextheading"
                        className="!font-medium text-primary-900"
                    >
                        Powered by
                    </AppText>
                    <Logo className="h-8 w-auto" />
                </a>

                <Button
                    leftIcon={<DownloadIcon className="h-4 w-4" />}
                    onClick={onDownload}
                >
                    Download Resume
                </Button>
            </nav>
            <div className="mx-auto mt-3 lg:hidden">
                <Button
                    className="!w-full"
                    leftIcon={<DownloadIcon className="h-4 w-4" />}
                    onClick={onDownload}
                >
                    Download Resume
                </Button>
            </div>
            <div
                className="flex w-full pt-14 lg:justify-center"
                onContextMenu={e => e.preventDefault()}
            >
                {error || !resumeUrl ? (
                    <div className="flex w-full justify-center pt-14">
                        <AppText
                            variant="contextheading"
                            className="text-error-500"
                        >
                            {error
                                ? "There was a problem loading your resume. Please refresh the page."
                                : "There was a problem loading your resume. Please provide a valid resume URL."}
                        </AppText>
                    </div>
                ) : !loading && pdfUrl && numPages > 0 ? (
                    <ReactPdfDocumentFile
                        file={pdfUrl}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                        loading={<LoadingComponent />}
                    >
                        {Array.from({ length: numPages }, (_, idx) => (
                            <div
                                key={idx}
                                style={{
                                    marginBottom:
                                        idx === numPages - 1 ? "0" : "20px",
                                }}
                            >
                                <ReactPdfPageCanvas
                                    pageNumber={idx + 1}
                                    width={800}
                                    renderMode="canvas"
                                    renderAnnotationLayer={false}
                                    renderForms={false}
                                    renderTextLayer={false}
                                />
                            </div>
                        ))}
                    </ReactPdfDocumentFile>
                ) : (
                    <LoadingComponent />
                )}
            </div>
        </div>
    );
};
